import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { classService } from '../services/classService';
import { schoolService } from '../services/schoolService';
import { School } from '../types';
import { CalendarIcon } from '../components/Icons';

const SchoolYearsPage: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [school, setSchool] = useState<School | null>(null);
  const [years, setYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [schoolId]);

  const fetchData = async () => {
    if (!schoolId) return;

    setLoading(true);
    try {
      // 获取学校信息
      const schoolData = await schoolService.getSchool(parseInt(schoolId));
      setSchool(schoolData);

      // 获取年份列表
      const yearsData = await classService.getAcademicYears(parseInt(schoolId));
      setYears(yearsData);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || '获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleYearClick = (year: string) => {
    navigate(`/schools/${schoolId}/years/${year}/classes`);
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/schools')}
          className="text-sm text-on-surface-variant hover:text-on-surface mb-4 flex items-center space-x-1"
        >
          <span>←</span>
          <span>返回学校列表</span>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-on-surface font-headline">
            {school?.name} - 学年列表
          </h1>
          <p className="text-sm text-on-surface-variant font-body mt-1">选择学年查看班级</p>
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* Years Grid */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-on-surface-variant font-body">加载中...</div>
        ) : years.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-on-surface-variant mb-4 flex justify-center">
              <CalendarIcon size={48} />
            </div>
            <p className="text-on-surface-variant font-body">暂无学年数据</p>
            <p className="text-sm text-on-surface-variant font-label mt-2">请先创建班级</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => handleYearClick(year)}
                className="group p-6 rounded-2xl shadow-soft hover:shadow-soft-md hover:bg-primary-50 transition-all text-left"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 group-hover:bg-primary-200 transition-colors">
                    <CalendarIcon size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-on-surface font-headline">{year}</h3>
                    <p className="text-sm text-on-surface-variant font-label">学年</p>
                  </div>
                </div>
                <div className="text-sm text-primary-600 font-medium group-hover:text-primary-700 font-label">
                  查看班级 →
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolYearsPage;
